import { ref, nextTick, Ref } from "vue";
import { useDebounce } from "vue-composable";

export interface MenuPositionProps {
  offset?: [number, number];
  maxHeight?: number;
}

export interface MenuPosition {
  top: string;
  left: string;
  width: string;
  height: string;
}

export function useMenuPosition(
  props: MenuPositionProps,
  target: Ref<HTMLElement | null>,
  content: Ref<HTMLElement | null>,
  menu: Ref<HTMLElement | null>,
) {
  const position = ref<MenuPosition>({ top: "0px", left: "0px", width: "0px", height: "auto" });
  const dragModalEl = ref<HTMLElement | null>(null);
  const isInsideDragModal = ref(false);

  const updatePosition = useDebounce(async function () {
    if (!content.value || !target.value) return;

    try {
      dragModalEl.value = target.value.closest("[data-root-draggable]") as HTMLElement | null;
      isInsideDragModal.value = !!dragModalEl.value;

      const rectInput = target.value.getBoundingClientRect();
      if (!rectInput) return;

      const modal = dragModalEl.value?.getBoundingClientRect();
      // When the target is a zero-width element (e.g. a caret marker span), the
      // rendered menu width is also 0. The outer .m-menu collapses (flex child
      // with overflow:auto has min-content-size 0), so menu.scrollWidth is also 0.
      // The inner .m-menu--content still reports the natural content scrollWidth, so
      // use that as fallback to get the real desired width.
      // ENG-21581 only fall back to content.scrollWidth when the menu itself is
      // collapsed (width 0). Otherwise a caller with a fixed `width` (e.g. a 300px
      // inline style) already renders at that width regardless of content, and
      // Math.max()-ing in the filtered content's unwrapped scrollWidth would use a
      // "desired" width the menu never actually renders at, throwing off the
      // right-edge clamp below as the content narrows/widens on every keystroke.
      const menuRectWidth = menu.value?.getBoundingClientRect().width ?? 0;
      const menuWidth = menuRectWidth > 0 ? menuRectWidth : content.value?.scrollWidth ?? 0;

      let top = rectInput.bottom + (props.offset?.[1] ?? 0);
      let left = rectInput.left + (props.offset?.[0] ?? 0);
      const width = Math.max(rectInput.width, menuWidth);

      const spaceBelow = window.innerHeight - rectInput.bottom - 30;
      let availableHeight =
        isInsideDragModal.value && modal ? props.maxHeight ?? spaceBelow : spaceBelow;

      if (left + width > window.innerWidth) {
        left = window.innerWidth - 5 - width;
      }

      if (isInsideDragModal.value && modal) {
        top = rectInput.bottom - modal.top;
        left = rectInput.left - modal.left;
      }

      await nextTick();

      // Flip above if less than 100px available below
      if (spaceBelow < 100) {
        // ENG-21581 cap the *actual* (already re-rendered) content height at maxHeight,
        // rather than substituting maxHeight outright. Using maxHeight outright assumes
        // the box always renders that tall, so as filtered content (e.g. one row instead
        // of ten) shrinks well below the cap, `top` keeps reserving the full maxHeight of
        // space above the input — leaving a growing gap between the shrunk box and the
        // input's top instead of staying flush against it.
        const naturalContentHeight = content.value.scrollHeight;
        const contentHeight = props.maxHeight
          ? Math.min(naturalContentHeight, props.maxHeight)
          : naturalContentHeight;
        availableHeight = Math.min(contentHeight, rectInput.top - 25);
        top = Math.max(25, rectInput.top - availableHeight - 5);

        if (isInsideDragModal.value && modal) {
          top = Math.max(25 - modal.top, rectInput.top - modal.top - availableHeight - 5);
        }
      }

      position.value = {
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        height: availableHeight > 0 ? `${availableHeight}px` : "auto",
      };
    } catch {
      // ignore
    }
  }, 1);

  return { position, dragModalEl, isInsideDragModal, updatePosition };
}
